import { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';

import { createTransferAgencyServices } from '@/api/AdditionalServices/createAdditionalServices';
import { fetchChatRoomList } from '@/api/chat/getChat';
import BasicButton from '@/components/Common/Button/BasicButton';
import ConfirmModal from '@/components/Common/Modal/ConfirmModal';
import MenuHeader from '@/components/Header/MenuHeader';
import { PhoneIcon } from '@/components/Icon';
import ApplyTransferAgencyServiceForm from '@/components/More/TransferAgencyService/ApplyTransferAgencyServiceForm';
import TransferGuideView from '@/components/More/TransferAgencyService/TransferGuideView';
import { IS_ALREADY_APPLY_TRANSFER_AGENCY_SERVICE } from '@/const/additionalService';
import { colors } from '@/const/colors';
import { REPRESENTATIVE_NUMBER } from '@/const/common';
import { COMMON_TOAST_ERROR } from '@/const/errorMessage';
import { useToastContext } from '@/contexts/Common/ToastContext';
import useAuthRedirect from '@/hooks/useAuthRedirect';
import useNavigateBack from '@/hooks/useNavigateBack';
import { memberAtom, updateAdditionalServiceAtom } from '@/store/member';
import { phoneCall } from '@/utils/common';
import { Fab } from '@mui/material';

export interface SelectedVehicleType {
  chatRoomId: number;
  productId: number;
  truckName: string;
}

const TransferAgencyService = () => {
  const { showToast } = useToastContext();

  const [memberAtomData] = useAtom(memberAtom);
  const [, updateAdditionalService] = useAtom(updateAdditionalServiceAtom);

  const [isNearBottom, setIsNearBottom] = useState(false);
  const threshold = 20;

  const [isAlreadyApply, setIsAlreadyApply] = useState(false);

  const authRedirect = useAuthRedirect();
  const navigateBack = useNavigateBack();
  const location = useLocation();
  const productInfo = location.state?.productInfo;

  const PurchaseAccompanyingServiceRef = useRef<HTMLDivElement>(null);

  const [isPopupShow, setIsPopupShow] = useState(false);
  const [requestForm, setRequestForm] = useState<AdditionalServicesApplyForm>();
  const [isDisabledRequestButton, setIsDisabledRequestButton] = useState(true);

  const updateRequestForm = (formData: AdditionalServicesApplyForm) => {
    setRequestForm(formData);
  };

  const {
    data: chattingList,
    isLoading,
    isFetching,
  } = useQuery<ChattingListResponse[]>(['get-chat-room-list'], () => fetchChatRoomList(), {
    onSuccess: () => {},
    onError: () => {
      showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
    },
    keepPreviousData: true,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!memberAtomData?.id,
  });

  useEffect(() => {
    if (memberAtomData) {
      setRequestForm({
        ...requestForm,
        name: { value: memberAtomData?.name, error: false },
        phoneNumber: { value: memberAtomData?.phoneNumber, error: false },
        productId: productInfo?.productId,
        truckName: productInfo?.truckName,
        chatRoomId: productInfo?.chatRoomId,
      });
      setIsAlreadyApply(memberAtomData.isAlreadyApplyTransferAgencyService);
    }
  }, [memberAtomData, productInfo]);

  const scrollBottom = () => {
    setTimeout(() => {
      if (PurchaseAccompanyingServiceRef.current) {
        window.scroll({
          top: PurchaseAccompanyingServiceRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 10);
  };

  const handleClickApplyService = () => {
    if (!authRedirect('/transfer-agency-service', { feature: '서류 이전 대행 신청' })) {
      return;
    }

    setIsPopupShow(true);
  };

  const handleClickClosePopup = () => {
    setIsPopupShow(false);
  };

  const handleClickOkBtn = () => {
    if (requestForm) {
      const requestData = {
        name: requestForm.name?.value,
        phoneNumber: requestForm.phoneNumber?.value,
        chatRoomId: requestForm.chatRoomId,
      };
      postPurchaseAccompanyingServices.mutate(requestData);
      setIsPopupShow(false);
    }
  };

  const postPurchaseAccompanyingServices = useMutation(
    (request: AdditionalServicesApplyRequest) => createTransferAgencyServices(request),
    {
      onSuccess: () => {
        showToast('서류 이전 대행 서비스를 신청했어요.', 'success', 'bottom');
        setIsAlreadyApply(true);
        updateAdditionalService(IS_ALREADY_APPLY_TRANSFER_AGENCY_SERVICE);
      },
      onError: (error: AxiosError) => {
        if (error.code && error.message) {
          showToast(error.message, 'error', 'bottom');
        } else {
          showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
        }
      },
    },
  );

  const handleClickPrev = () => {
    navigateBack();
  };

  const handleScroll = () => {
    const scrollTop = window.scrollY;
    const viewportHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    setIsNearBottom(scrollTop + viewportHeight >= documentHeight - threshold);
  };

  const handleScrollBottom = () => {
    const currentScroll = window.scrollY;
    const viewportHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    if (currentScroll + viewportHeight < documentHeight) {
      window.scrollBy({
        top: viewportHeight,
        left: 0,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    handleScroll();
  }, []);

  useEffect(() => {
    setIsDisabledRequestButton(requestForm?.name?.error || requestForm?.phoneNumber?.error || false);
  }, [requestForm?.name?.error || requestForm?.phoneNumber?.error, isLoading]);

  return (
    <>
      <MenuHeader title="서류 이전 대행 서비스" onClickPrevBtn={handleClickPrev}></MenuHeader>

      <div className="w-full pt-[60px] bg-gray-1 pb-[100px]" ref={PurchaseAccompanyingServiceRef}>
        <TransferGuideView />
        <ApplyTransferAgencyServiceForm
          isShowVehicleSelector={(chattingList && chattingList?.length > 0) || false}
          formData={requestForm || {}}
          updateFormData={(data: AdditionalServicesApplyForm) => updateRequestForm(data)}
        />

        <div className="px-4 pt-6">
          <p className="text-gray-7 pb-5 text-pretty font-medium text-[14px]">
            [안내] 명의 이전에 필요한 서류 및 절차는 법인명의 영업용번호판, 개인명의 영업용번호판에 따라 상이하므로
            서비스 신청 후 별도의 안내를 드리고 있습니다.
          </p>
          <p className="text-gray-7 pb-5 text-pretty font-medium text-[14px]">
            * 서류 이전 대행 서비스는 알선판매 서비스 내에 포함된 서비스입니다. 직거래 알선판매 서비스 이용 시 이전 대행
            서비스도 함께 제공되며 이전 대행 서비스 단독 이용 시 알선판매 서비스는 제공되지 않습니다.
          </p>
        </div>

        <div className="flex items-center justify-center fixed w-full mx-auto my-0 px-4 pb-2 bottom-0 left-0 right-0 h-14 max-w-[720px] min-w-[280px] bg-white">
          <div className="absolute right-[16px] top-[-72px]">
            <Fab
              sx={{
                backgroundColor: colors.gray[0],
                width: 52,
                height: 52,
                borderRadius: '50px',
                '&:hover': {
                  backgroundColor: colors.gray[0],
                },
                zIndex: 100,
              }}
              onClick={() => phoneCall(REPRESENTATIVE_NUMBER)}
            >
              <PhoneIcon color={colors.primary[10]}></PhoneIcon>
            </Fab>
          </div>

          <div className="absolute top-[-16px] left-0 w-full h-[16px] bg-gradient-to-b from-transparent to-[#ffffff]"></div>
          <BasicButton
            name={isAlreadyApply ? '서비스 신청완료' : isNearBottom ? '서비스 신청하기' : '아래로 내리기'}
            bgColor={colors.primary[10]}
            borderColor={colors.primary[10]}
            textColor={colors.gray[0]}
            fontSize={16}
            height={48}
            fontWeight={'bold'}
            isDisabled={isAlreadyApply || (isNearBottom && isDisabledRequestButton)}
            onClick={isNearBottom ? handleClickApplyService : handleScrollBottom}
          />
        </div>
      </div>

      <ConfirmModal
        isOpen={isPopupShow}
        title={`${!!requestForm?.productId ? requestForm?.truckName : ''}`}
        content={
          <span>
            {requestForm?.productId ? (
              <>
                선택한 차량으로 서류 이전 대행
                <br />
                서비스를 신청할까요?
              </>
            ) : (
              <>
                서류 이전 대행 서비스를
                <br />
                신청하시겠어요?
              </>
            )}
          </span>
        }
        rightButtonLabel="신청하기"
        onLeftButtonClick={handleClickClosePopup}
        onRightButtonClick={handleClickOkBtn}
      />
    </>
  );
};

export default TransferAgencyService;
